import axios from "axios";
import { CircleAlert as CircleAlertIcon, CircleCheck as CircleCheckIcon, EllipsisVertical as EllipsisVerticalIcon, NotebookPen as NotebookPenIcon, PencilLine as PencilLineIcon, Plus as PlusIcon, ShoppingCart as ShoppingCartIcon } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";

import { useCurrentUser } from "base/app";
import { MILLIS_IN_A_DAY } from "base/constants";
import EventTracker, { PAGE_EVENTS } from "base/eventsTracker";
import { WalletLedgerEntries } from "base/payments";
import { ChatSession } from "base/ui/chat";
import { measureDuration, millisToDateString, DateView } from "base/ui/date";
import { GenericException } from "base/ui/errors";
import { geoDistance } from "base/ui/location";
import { RoundUserImage } from "base/ui/misc";
import { Popup } from "base/ui/popups";
import { LoadingOverlay } from "base/ui/status";
import { round } from "base/utils";
import { doClickOnEnter, loadGoogleMapsScript } from "base/utils/common";
import { broadcaster, useBroadcastedState } from "base/utils/events";

import AddVisitSearch from "./AddVisitSearch";
import CustomerVisit, { useActiveCustomerVisit } from "./CustomerVisit";
import { INBOX_MESSAGE_TYPE_ROUTE_PLAN, INBOX_MESSAGE_TYPE_STAFF_GPS, ORG_FEATURE_FLAGS } from "../../../../constants";
import { getFeatureFlag } from "../../../../helpers/StoreHelper";
import WarningIcon from "../../../../images/warning.svg?url";
import { CHECKIN_STATUS, STAFF_LOCATION_LOGGED_EVENT } from "../../../../useStaffLocation";
import { UserBadge } from "../../../ui/commonUI";
import ResponsivePopup from "../../../ui/ResponsivePopup";
import CustomersList, { showAddCustomerPopup } from "../../customers/CustomersList";
import {getDeliveryRouteUsersCached} from "../../customers/DeliveryRouteUser";

const CUSTOME_STORE_RADIUS = 0.02; // Kms
const CHECKIN_MARKER = { background: "#0CB800", borderColor: "#098B00", glyphColor: "#098B00", scale: 0.6 };
const CHECKOUT_MARKER = { scale: 0.6 };
const ACTIVITY_MARKER = { background: "#F48400", borderColor: "#C96D00", glyphColor: "#C96D00", scale: 0.7 };
const POLYLINE = { geodesic: true, strokeColor: "#00B2F5", strokeOpacity: 1.0, strokeWeight: 2 };

function getApproxTimeSpent(gps_logs, i) {
	const prev = gps_logs
		.slice(i + 1)
		.find((log) => geoDistance(...gps_logs[i].data.lat_long, ...log.data.lat_long, "K") >= CUSTOME_STORE_RADIUS);
	const next = gps_logs
		.slice(0, i)
		.reverse()
		.find((log) => geoDistance(...gps_logs[i].data.lat_long, ...log.data.lat_long, "K") >= CUSTOME_STORE_RADIUS);
	if (prev && next) {
		return next.created_at - prev.created_at;
	}
	return null;
}

/* customers without a name (or no longer on the route) still need a visible label */
function getCustomerLabel(customer) {
	return customer?.name || customer?.phone_number || "";
}

function CheckedInButton({ onClick }) {
	return (
		<button
			className="tw-bg-green-700 tw-text-white tw-text-sm tw-px-3 tw-py-1.5 tw-rounded"
			onClick={onClick}
			type="button"
		>
			Checked in
		</button>
	);
}

export function TrackStaffLocation({ delivery_route_id, staff, date: _date, hide_summary }) {
	const session_id = `staff_notes_${delivery_route_id}_${staff._id}`;
	const [gps_logs, setGpsLogs] = useState([]);
	const [planned_visits, setPlannedVisits] = useState([]);
	const [customer_visits, setCustomerVisits] = useState([]);
	const [date, setDate] = useState(_date || new Date());
	const [map, setMap] = useState(null);
	const [staff_activity, setStaffActivity] = useState(null);
	const [active_tab, setActiveTab] = useState(0);
	const ctx = useRef({}).current;
	const [is_loading, setLoading] = useState(false);
	const user = useCurrentUser();
	const [edit_route_plan, setEditRoutePlan] = useState(false);
	const [org] = useBroadcastedState("org");
	const is_manual_visit_enabled = getFeatureFlag(ORG_FEATURE_FLAGS.ROUTE_PLAN_MANUAL_CHECKIN_CHECKOUT, org)[0];
	const is_shop_photo_enabled = getFeatureFlag(ORG_FEATURE_FLAGS.ROUTE_PLAN_SHOP_PHOTO, org)[0];
	const [active_visit] = useActiveCustomerVisit();
	const [visit_customer, setVisitCustomer] = useState(null);
	const [show_add_visit, setShowAddVisit] = useState(false);
	const can_manage_visits = is_manual_visit_enabled && user?._id === staff._id;
	const [screen] = useBroadcastedState("tw_screen_size");
	const [selected_delivery_route] = useBroadcastedState("selected_delivery_route");

	useEffect(() => {
		if (_date) {
			setDate(_date);
		}
	}, [_date]);

	const openCustomerNotes = (user_id) => {
		EventTracker.track(PAGE_EVENTS.USER_NOTES_INITIATED);
		ChatSession.open(
			`customer_notes_${delivery_route_id}_${user_id}`
		)
	}

	const getMessages = () => {
		if (ctx.has_more === false || ctx.is_loading) return;
		setLoading(true);
		ctx.is_loading = true;
		const start_time = new Date(date).setUTCHours(0, 0, 0, 0);
		axios
			.post("/session/messages", {
				less_than_timestamp: ctx.next_less_than_timestamp || start_time + MILLIS_IN_A_DAY,
				greater_than_timestamp: start_time - 1,
				session_id: session_id,
			})
			.then((resp) => {
				if (resp.data.error_message) {
					return;
				}
				ctx.has_more = resp.data.has_more;
				/* GPS logs */
				const logs = resp.data.inbox_messages?.filter((m) => m._type === INBOX_MESSAGE_TYPE_STAFF_GPS) || [];
				ctx.gps_logs = [...(ctx.gps_logs || []), ...logs];
				setGpsLogs(ctx.gps_logs);

				/* Route Plan */
				const route_plan = resp.data.inbox_messages?.find(
					(m) => m._type === INBOX_MESSAGE_TYPE_ROUTE_PLAN
				);
				if (route_plan?.data?.customer_ids) {
					getDeliveryRouteUsersCached(
						delivery_route_id, route_plan.data.customer_ids
					).then((cached_users_map) => {
						const users = {};
						route_plan.data.customer_ids.forEach((user_id) => {
							users[user_id] = cached_users_map[user_id] || { _id: user_id };
						});
						setPlannedVisits(users);
					});
				}

				ctx.is_loading = false;
				ctx.next_less_than_timestamp = resp.data.next_less_than_timestamp;
				if (ctx.has_more) {
					getMessages();
				} else if (ctx.pending_refetch !== undefined) {
					const clear_route_plan = ctx.pending_refetch;
					ctx.pending_refetch = undefined;
					ctx.refetchMessages(clear_route_plan);
				}
			})
			.finally(() => {
				ctx.is_loading = false;
				setLoading(false);
			});
	};

	const initializeMap = async () => {
		const { Map } = await window.google.maps.importLibrary("maps");
		const { PinElement, AdvancedMarkerElement } = await window.google.maps.importLibrary("marker");
		const map = new Map(document.getElementById("staff_map"), {
			center: { lat: -1.286, lng: 36.817 },
			zoom: 10,
			mapId: "7651a7a9552a860c",
			fullscreenControlOptions: {
				position: window.google.maps.ControlPosition.BOTTOM_RIGHT,
			},
		});
		map.AdvancedMarkerElement = AdvancedMarkerElement;
		map.PinElement = PinElement;
		setMap(map);
	};


	useEffect(() => {
		if (map && !ctx.is_loading) {
			ctx.markers?.forEach((m) => m.setMap(null));
			ctx.markers = [];
			ctx.info_windows = {};
			ctx.line?.setMap(null);
			const activity = {
				total_distance: 0,
				total_time: 0,
				total_orders: 0,
				customer_visits: {},
			};

			if (gps_logs.length > 0) {
				const bounds = new window.google.maps.LatLngBounds();
				ctx.line = new window.google.maps.Polyline(POLYLINE);
				ctx.line.setMap(map);
				const line_path = ctx.line.getPath();

				// Calculating total time
				activity.total_time = gps_logs[0].created_at - gps_logs[gps_logs.length - 1].created_at;

				const check_in = gps_logs.findLastIndex((log) => log.data?.info?.status === CHECKIN_STATUS.CHECKED_IN);
				if (check_in >= 0) gps_logs[check_in].is_checkin = true;
				const check_out = gps_logs.findIndex((log) => log.data?.info?.status === CHECKIN_STATUS.NEED_CHECK_IN);
				if (check_out >= 0 && check_out < check_in) gps_logs[check_out].is_checkout = true;

				gps_logs.forEach((log, i) => {
					// Calculating Total distance
					activity.total_distance +=
						i === gps_logs.length - 1 ? 0 : geoDistance(...log.data.lat_long, ...gps_logs[i + 1].data.lat_long, "K");

					// Setting bounds, line,
					const lat_long = new window.google.maps.LatLng(...log.data.lat_long);
					bounds.extend(lat_long);
					line_path.push(lat_long);

					// Setting check-in and check-out marker
					if (log.is_checkin) {
						const info_content = `<div id="checkin_content" className="tw-p-4">Check-in at ${millisToDateString(log.created_at)}</div>`;
						const infowindow = new window.google.maps.InfoWindow({ content: info_content });
						const marker = new map.AdvancedMarkerElement({
							map,
							position: lat_long,
							content: new map.PinElement(CHECKIN_MARKER).element,
						});
						marker.addListener("click", () => {
							infowindow.open({ anchor: marker, map });
						});
						ctx.markers.push(marker);
					} else if (log.is_checkout) {
						const info_content = `<div id="checkout_content" className="tw-p-4">Check-out at ${millisToDateString(log.created_at)}</div>`;
						const infowindow = new window.google.maps.InfoWindow({ content: info_content });
						const marker = new map.AdvancedMarkerElement({
							map,
							position: lat_long,
							content: new map.PinElement(CHECKOUT_MARKER).element,
						});
						marker.addListener("click", () => {
							infowindow.open({ anchor: marker, map });
						});
						ctx.markers.push(marker);
					}

					// Setting activity markers, Tracking customer visits and time-spent
					if (log.data.info?.buying_for_user_id) {
						const { info } = log.data;
						const customer_visit = activity.customer_visits[info.buying_for_user_id] || {
							orders: [],
							lat_long,
							time_spent: getApproxTimeSpent(gps_logs, i),
						};
						if (info.order_id && !customer_visit.orders.includes(info.order_id)) {
							customer_visit.orders.push(info.order_id);
							activity.total_orders += 1;
						}
						if (info.is_customer_notes) {
							customer_visit.is_customer_notes = true;
						}
						activity.customer_visits[info.buying_for_user_id] = customer_visit;
					}
				});

				Object.entries(activity.customer_visits).forEach(([key, value], i) => {
					const { time, measure } = value.time_spent ? measureDuration(value.time_spent) : {};
					const info_content = `<div id="activity_${i}">Customer: <b>${key}</b><br />Send Cart Orders: <b>${value.orders.length}</b><br />Notes Taken: <b>${value.is_customer_notes ? "YES" : "NO"}</b><br/>Time Spent: <b>${time || "-"} ${measure || "-"}</b></div>`;
					const infowindow = new window.google.maps.InfoWindow({ content: info_content });
					const marker = new map.AdvancedMarkerElement({
						map,
						position: value.lat_long,
						content: new map.PinElement(ACTIVITY_MARKER).element,
					});
					marker.addListener("click", () => {
						infowindow.open({ anchor: marker, map });
					});
					ctx.markers.push(marker);
					ctx.info_windows[key] = infowindow;
				});

				const user_ids = Object.keys(activity.customer_visits);
				getDeliveryRouteUsersCached(
					delivery_route_id, user_ids					
				).then((cached_users_map) => {
					user_ids.forEach((user_id) => {
						activity.customer_visits[user_id].user = cached_users_map[user_id];
					});
					setCustomerVisits({ ...activity.customer_visits });
					Object.entries(ctx.info_windows).forEach(([key, infowindow]) => {
						let { content } = infowindow;
						content = content.replace(key, cached_users_map[key]?.name);
						infowindow.setContent(content);
					});
				});
				setStaffActivity(activity);
				map.fitBounds(bounds);
			}
		}
	}, [map, gps_logs]);

	const refetchMessages = (clear_route_plan = true) => {
		if (ctx.is_loading) {
			/* undefined = nothing pending, otherwise whether the queued refetch should clear the plan */
			ctx.pending_refetch = ctx.pending_refetch === true || clear_route_plan;
			return;
		}
		setStaffActivity(null);
		ctx.has_more = null;
		ctx.gps_logs = [];
		ctx.next_less_than_timestamp = null;
		/* keep the plan while refreshing activity, so an open visit screen stays mounted */
		if (clear_route_plan) setPlannedVisits([]);
		setCustomerVisits([]);
		getMessages();
	};
	ctx.refetchMessages = refetchMessages;

	useEffect(() => {
		if (staff && date) {
			refetchMessages();
		}
	}, [staff, session_id, date]);

	useEffect(() => {
		loadGoogleMapsScript("AIzaSyCSMLgYj7Uxz34Afcvclun1FslKy6ILtwI").then(() => {
			initializeMap();
		});
		/* listeners are bound once, so always call the latest closure (date/staff can change) */
		const onChatMessageSent = () => ctx.refetchMessages(false);
		const onStaffLocationLogged = (logged) => {
			logged?.delivery_route_id === delivery_route_id && ctx.refetchMessages(false);
		};
		broadcaster.add_event_listener("chat:message_sent", onChatMessageSent);
		broadcaster.add_event_listener(STAFF_LOCATION_LOGGED_EVENT, onStaffLocationLogged);
		return () => {
			broadcaster.remove_event_listener("chat:message_sent", onChatMessageSent);
			broadcaster.remove_event_listener(STAFF_LOCATION_LOGGED_EVENT, onStaffLocationLogged);
		};
	}, []);

	const saveRoutePlan = () => {
		if (edit_route_plan?.length > 0) {
			setLoading(true);
			axios
				.post(`/api/admin/staff/route_plan/${delivery_route_id}`, {
					customer_ids: edit_route_plan,
					date: date.getTime(),
					staff_user_id: staff._id,
				})
				.then((resp) => {
					if (resp.data?.logged) {
						refetchMessages();
					}
					setEditRoutePlan(null);
				})
				.finally(() => setLoading(false));
		}
	};

	const openNotes = (user) => {
		ChatSession.open(`customer_notes_${delivery_route_id}_${user._id}`);
	};
	const addNewShop = () => {
		/* full route object is only available on admin pages, it's used for area/tag suggestions */
		const delivery_route = selected_delivery_route?._id === delivery_route_id
			? selected_delivery_route
			: { _id: delivery_route_id };
		showAddCustomerPopup(delivery_route, (new_user) => {
			if (delivery_route.users) {
				delivery_route.users.unshift(new_user);
				delivery_route.users_count += 1;
			}
			Popup.toast(<div className="tw-rounded tw-p-2 tw-text-sm">{new_user.name || "Shop"} added</div>, 2500);
		});
	};
	const buyForCustomer = (user) => {
		window.open(`/${delivery_route_id}?buying_for_user_id=${user._id}`, "_self");
	};
	const checkInCustomer = (user) => {
		var popup = Popup.show(
			"Customer Check In",
			<div className="tw-text-sm tw-p-4">
				To Check In, you must place an order for the customer or take any notes.
				<div className="tw-flex tw-mt-6 tw-gap-4 tw-justify-end">
					<button
						className="btn-primary-v2 tw-flex tw-gap-x-1.5 tw-items-center hover:tw-flex"
						onClick={() => {
							popup.close();
							buyForCustomer(user);
						}}
						type="button"
					>
						<ShoppingCartIcon size={14} />
						Send Cart
					</button>
					<button
						className="btn-secondary tw-flex tw-gap-x-1.5 tw-items-center"
						onClick={() => {
							popup.close();
							openNotes(user);
						}}
						type="button"
					>
						<PencilLineIcon size={14} />
						Notes
					</button>
				</div>
			</div>,
		);
	};

	const { time: total_time, measure } = measureDuration(staff_activity?.total_time || 0);
	const additional_visits = Object.keys(customer_visits || {}).filter((user_id) => !planned_visits[user_id]);
	const isActiveVisit = (user_id) => (
		active_visit?.customer_id === user_id && active_visit?.delivery_route_id === delivery_route_id
	);

	return (
		<>
			{is_loading ? <LoadingOverlay /> : null}
			{_date ? null : (
				<div className="lg:tw-hidden tw-bg-white tw-rounded tw-text-sm tw-py-2 tw-px-4 tw-border tw-w-fit tw-ml-4 tw-my-2">
					<input
						className="tw-outline-0"
						type="date"
						defaultValue={date ? date.toISOString().slice(0, 10) : ""}
						onChange={(evt) => setDate(new Date(evt.target.value))}
					/>
				</div>
			)}
			{/* Summary  */}
			{!hide_summary ? (
				<div className="tw-flex tw-items-center max-lg:tw-flex-wrap tw-py-2 tw-bg-primary-fade tw-bg-opacity-50 tw-pr-4">
					<div className="hflex tw-gap-4 lg:tw-border-r-2 tw-pr-4 tw-grow">
						<UserBadge user={staff} />
						{date.getTime() >= new Date().setHours(0, 0, 0, 0) ? (
							<button
								className="btn-primary-v2 tw-text-xs lg:tw-text-sm"
								onClick={() => setEditRoutePlan(Object.keys(planned_visits || {}))}
								type="button"
							>
								{Object.keys(planned_visits || {}).length === 0 ? "+ Create" : "Edit"} Route Plan
							</button>
						) : null}
					</div>
					<div className="max-lg:tw-grid max-md:tw-grid-cols-3 md:tw-grid-cols-5 max-lg:tw-gap-y-4 lg:tw-flex">
						<div className="tw-px-4 tw-border-r">
							<b>{planned_visits ? Object.keys(planned_visits).length : 0}</b>
							<p className="tw-text-xs tw-text-gray-500">Planned Visits</p>
						</div>
						<div className="tw-px-4 tw-border-r">
							<b>{staff_activity ? Object.keys(staff_activity.customer_visits).length : 0}</b>
							<p className="tw-text-xs tw-text-gray-500">Visits Completed</p>
						</div>
						<div className="tw-px-4 tw-border-r">
							<b>{Number(staff_activity?.total_distance || 0).toFixed(2) || 0}</b>
							<p className="tw-text-xs tw-text-gray-500">Total Distance (KMs)</p>
						</div>
						<div className="tw-px-4 tw-border-r">
							<b>{total_time}</b>
							<p className="tw-text-xs tw-text-gray-500">Time on Route ({measure})</p>
						</div>
						<div className="tw-px-4">
							<b>{staff_activity?.total_orders || 0}</b>
							<p className="tw-text-xs tw-text-gray-500">Orders</p>
						</div>
					</div>
				</div>
			) : null}
			<div className="md:tw-hidden tw-grid tw-grid-cols-2 tw-text-center tw-pt-4 tw-px-4">
				<p
					className={`tw-pb-2 ${active_tab === 0 ? "tw-border-b-2 tw-text-secondary-lighter tw-font-bold tw-border-secondary-lighter" : ""}`}
					onClick={() => setActiveTab(0)}
				>
					Route Plan
				</p>
				<p
					className={`tw-pb-2 ${active_tab === 1 ? "tw-border-b-2 tw-text-secondary-lighter tw-font-bold tw-border-secondary-lighter" : ""}`}
					onClick={() => setActiveTab(1)}
				>
					Track Location
				</p>
			</div>
			<div className="tw-flex tw-border-t tw-px-4">
				<div className={`tw-w-full md:tw-w-[80%] ${active_tab === 1 ? "max-md:tw-hidden" : ""}`}>
					<div className="tw-pr-4 tw-py-2 tw-text-xs tw-text-primary tw-font-bold tw-tracking-wide tw-mt-6">
						PLANNED VISITS ({Object.keys(planned_visits || {}).length})
					</div>
					<div className="tw-divide-y">
						{Object.keys(planned_visits || {}).map((user_id) => (
							<div className="tw-px-4 tw-py-2 hflex tw-gap-4" key={user_id}>
								<div className="tw-flex tw-gap-2 tw-items-center">
									<span className="tw-ml-2">{getCustomerLabel(planned_visits[user_id])}</span>
									<NotebookPenIcon size={18} onClick={() => openCustomerNotes(user_id)} className="tw-cursor-pointer" />
								</div>
								<div className="tw-flex tw-gap-2 tw-items-center">
									{
										planned_visits[user_id]?.orders?.length > 0 ? (
											<span className="tw-bg-primary-v2 tw-text-white tw-text-xs tw-px-2 tw-py-1 tw-rounded-full">Ordered</span>
										) : null
									}
									{can_manage_visits && isActiveVisit(user_id) ? (
										<CheckedInButton onClick={() => setVisitCustomer(planned_visits[user_id])} />
									) : !staff_activity?.customer_visits?.[user_id] ? (
										user._id === staff._id ? (
											<button
												className="btn-primary-v2"
												onClick={() => (
													is_manual_visit_enabled
														? setVisitCustomer(planned_visits[user_id])
														: checkInCustomer(planned_visits[user_id])
												)}
												type="button"
											>
												Start Visit
											</button>
										) : (
											<CircleAlertIcon size={18} className="tw-text-amber-600" />
										)
									) : (
										<CircleCheckIcon size={18} className="tw-text-green-600" />
									)}
								</div>
							</div>
						))}
					</div>
					<div className="hflex tw-pr-4 tw-py-2 tw-text-xs tw-text-primary tw-font-bold tw-border-t tw-border-dashed tw-tracking-wide tw-mt-6">
						<span>ADDITIONAL VISITS ({additional_visits.length})</span>
						{can_manage_visits ? (
							<button
								className="tw-flex tw-items-center tw-gap-1 tw-px-2 tw-py-1 tw-rounded tw-border tw-border-green-700 tw-text-green-800 tw-tracking-normal"
								onClick={() => setShowAddVisit(true)}
								type="button"
							>
								<PlusIcon size={14} />
								Add
							</button>
						) : null}
					</div>
					<div className="tw-divide-y">
						{additional_visits.map((user_id) => (
							<div className="tw-px-4 tw-py-2 hflex tw-gap-4" key={user_id}>
								<div className="tw-flex tw-gap-2 tw-items-center">
									<span className="tw-ml-2">{getCustomerLabel(staff_activity?.customer_visits[user_id]?.user)}</span>
									<NotebookPenIcon size={18} onClick={() => openCustomerNotes(user_id)} className="tw-cursor-pointer" />
								</div>
								<div className="tw-flex tw-gap-2 tw-items-center">									
									{
										staff_activity?.customer_visits[user_id]?.orders?.length > 0 ? (
											<span className="tw-border tw-border-primary-v2 tw-text-primary-v2 tw-text-xxs tw-px-2 tw-py-1 tw-rounded-full">Ordered</span>
										) :null
									}
									{can_manage_visits && isActiveVisit(user_id) ? (
										<CheckedInButton
											onClick={() => setVisitCustomer(
												staff_activity?.customer_visits[user_id]?.user
												|| { _id: user_id, name: active_visit.customer_name },
											)}
										/>
									) : (
										<CircleCheckIcon size={18} className="tw-text-green-600" />
									)}
								</div>
							</div>
						))}
					</div>
					{!screen.md ? (
						<div className="tw-sticky tw-bottom-0 tw--mx-4 tw-mt-4 tw-px-4 tw-py-3 tw-bg-white tw-border-t">
							<button
								className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-3 tw-rounded-xl tw-border-2 tw-border-dashed tw-border-green-700 tw-text-green-800 tw-font-bold"
								onClick={addNewShop}
								type="button"
							>
								<PlusIcon size={18} />
								Add a new shop
							</button>
						</div>
					) : null}
				</div>
				<div
					id="staff_map"
					className={`tw-h-[420px] tw-w-full tw-my-2 ${active_tab === 0 ? "max-md:tw-hidden" : ""}`}
					onKeyUp={doClickOnEnter}
				/>
			</div>
			<ResponsivePopup
				is_full_screen={true}
				show={edit_route_plan}
				onClose={() => setEditRoutePlan(null)}
				title={`${edit_route_plan?.length ? "Edit" : "Create"} Route Plan`}
				style_props={{z_index: 50}}
			>
				<div className="tw-pb-12">
					<div className="hflex tw-pr-4 tw-py-2 tw-bg-gray-bg">
						<UserBadge user={staff} />
						<span>
							<DateView millis={date.getTime()} exclude_time={true} />
						</span>
					</div>
					<div className="tw-border-t tw-px-4 tw-py-2">
						<CustomersList is_select_list={true} selected_users={edit_route_plan} 
							setSelectedUsers={setEditRoutePlan} 
						/>
					</div>
					<div className="tw-absolute tw-bottom-0 tw-w-full tw-px-4 tw-py-2 tw-bg-white tw-border-t hflex tw-gap-4 !tw-justify-end">
							<span className="tw-text-xs tw-text-gray-500">{edit_route_plan?.length} selected&nbsp;&nbsp;</span>
							<button className="btn-tertiary tw-text-sm" onClick={() => setEditRoutePlan(null)} type="button">
								Cancel
							</button>
							<button className="btn-primary-v2 tw-text-sm" onClick={saveRoutePlan} type="button">
								Save
							</button>
					</div>
				</div>
			</ResponsivePopup>
			{can_manage_visits ? (
				<ResponsivePopup
					is_full_screen={true}
					show={show_add_visit}
					onClose={() => setShowAddVisit(false)}
					title="Add Visit"
					style_props={{ z_index: 50 }}
				>
					<AddVisitSearch
						delivery_route_id={delivery_route_id}
						onMarkVisited={(customer) => {
							setShowAddVisit(false);
							setVisitCustomer(customer);
						}}
					/>
				</ResponsivePopup>
			) : null}
			{is_manual_visit_enabled && visit_customer ? (
				<CustomerVisit
					delivery_route_id={delivery_route_id}
					customer={visit_customer}
					stop_number={Object.keys(planned_visits).indexOf(visit_customer._id) + 1}
					total_stops={Object.keys(planned_visits).length}
					gps_logs={gps_logs}
					require_shop_photo={is_shop_photo_enabled}
					onClose={() => setVisitCustomer(null)}
					onTakeOrder={buyForCustomer}
					onAddNote={(customer) => {
						setVisitCustomer(null);
						openNotes(customer);
					}}
				/>
			) : null}
		</>
	);
}

function StaffUser({ delivery_route_short, user, user_type, removeStaff, editStaffPopup, onFinanceManagerWalletUpdate }) {
	const [staff_wallet, setStaffWallet] = useState(user.staff_wallet);
	const current_user = useCurrentUser();

	const is_route_plan_enabled = getFeatureFlag(ORG_FEATURE_FLAGS.ROUTE_PLAN, delivery_route_short.delivery_org)[0]
	
	const depositMoneyToFinanceManager = () => {
		let to_deposit = parseInt(window.prompt("Deposit into Finance Manager's wallet for the offline cash collected."));
		if (isNaN(to_deposit)) return;
		const reason = window.prompt("Reason for collecting cash (optional):") || undefined;
		axios
			.post(`/api/admin/delivery_route/${delivery_route_short._id}/user/${user._id}/collect_cash`, {
				amount: to_deposit,
				comment: reason,
			})
			.then((resp) => {
				if (resp.data.errors) {
					Popup.show("Errors", <GenericException ex={resp.data.errors} />);
					return;
				}
				Popup.toast(
					<div className="tw-rounded tw-p-2 tw-text-sm">
						Deposited {to_deposit} {delivery_route_short.currency} into the Finance Manager wallet.
					</div>,
					2500,
				);

				setStaffWallet(resp.data.paying_staff_wallet);
				onFinanceManagerWalletUpdate?.(resp.data.finance_manager_wallet);
			});
	};

	const viewLedger = () => {
		Popup.show("Ledger Entries", <WalletLedgerEntries wallet={staff_wallet} />);
	};

	const openStaffNotes = () => {
		ChatSession.open(`staff_notes_${delivery_route_short._id}_${user._id}`);
	};

	const trackLocation = () => {
		Popup.show(
			"Track Location", 
			<TrackStaffLocation delivery_route_id={delivery_route_short._id} staff={user} />
		);
	};

	const showOptions = (evt) => {
		Popup.showContextMenu(
			evt.target,
			<div className="w3-list w3-list-bordered">
				{
					current_user?.roles?.finance_manager && staff_wallet?.wallet_amount < 0
						? <div className="w3-padding-8 tw-cursor-pointer" onClick={depositMoneyToFinanceManager}>
							Collect Cash
						</div>
						: null
				}
				{
					staff_wallet?.user_id
						? <div className="w3-padding-8 tw-cursor-pointer" onClick={viewLedger}>
							View Ledger
						</div>
						: null
				}
				<div className="w3-padding-8 tw-cursor-pointer" onClick={trackLocation}>
					Track Location
				</div>
				<div className="w3-padding-8 tw-cursor-pointer" onClick={openStaffNotes}>
					Staff Notes{" "}
				</div>
			</div>,
		);
	};

	return (
		<div className="tw-flex tw-py-2 tw-flex-wrap" key={user._id} onClick={showOptions}>
			<div className="tw-w-10 tw-h-10 tw-rounded-full flex-box tw-overflow-hidden">
				<RoundUserImage user={user} />
			</div>
			<div className="tw-grow tw-px-2">
				<div className="tw-flex tw-items-center">
					<div className="tw-text-gray-600 tw-text-sm tw-font-bold">{user.name}</div>
					{
						user.roles?.finance_manager
							? <div className="tw-ml-4 tw-text-xs tw-px-2 tw-py-[2px] tw-text-white tw-rounded tw-bg-green-600">
								Finance Manager
							</div>
							: null
					}
				</div>
				<div className="tw-text-gray-500 tw-text-sm">{user.phone_number || user.email_id}</div>
			</div>
			{
				staff_wallet
					? <div
						className={`tw-flex-none tw-text-sm tw-self-center tw-mx-10 ${staff_wallet.wallet_amount < 0 ? "tw-text-red-600" : "tw-text-gray-600"}`}
					>
						{round(staff_wallet.wallet_amount)} {staff_wallet.wallet_currency}
					</div>
					: null
			}
			<div
				className="tw-flex tw-flex-row tw-flex-wrap tw-gap-3 tw-items-center"
				onClick={(event) => event.stopPropagation()}
			>
				<EllipsisVerticalIcon
					size={18}
					className="tw-cursor-pointer"
					onClick={(evt) => {
						evt.stopPropagation();
						let vertical_menu = null;
						vertical_menu = Popup.showContextMenu(
							evt.target,
							<div>
								<div
									className="tw-cursor-pointer hover:tw-bg-gray-100 tw-border-b tw-border-gray-200 tw-p-2.5"
									onClick={(event) => {
										event.stopPropagation();
										editStaffPopup(user, user_type);
										vertical_menu.close();
									}}
								>
									Edit
								</div>
								<div
									className="tw-cursor-pointer hover:tw-bg-gray-100 tw-p-2.5 tw-text-red-500"
									onClick={(event) => {
										event.stopPropagation();
										let user_confirm_popup = null;
										user_confirm_popup = Popup.open(
											"",
											<div className="tw-p-7 tw-flex tw-flex-col tw-justify-center tw-items-center">
												<img src={WarningIcon} width="60px" alt="warning-icon" />
												<div className="tw-text-lg tw-font-semibold tw-p-2 tw-text-center tw-mt-4">
													Are you sure you want to remove?
												</div>
												<p className="tw-text-[14px] tw-font-normal tw-text-center tw-mt-2 tw-text-secondary-text">You will not be able to retrieve this once removed, Please check again.</p>
												<div className="tw-flex tw-flex-row tw-justify-center tw-flex-wrap tw-gap-5 tw-mt-7 tw-w-full">
													<button
														className="btn-primary !tw-bg-[#C01616] tw-w-full"
														onClick={() => {
															removeStaff(user, user_type);
															user_confirm_popup.close();
														}}
														type="button"
													>
														Remove
													</button>
												</div>
											</div>,
											{ className: "md:tw-w-[400px]" },
										);
										vertical_menu.close();
									}}
								>
									Remove
								</div>
							</div>,
						);
					}}
				/>
			</div>
		</div>
	);
}

export default StaffUser;
